import { ArrowLeft, RefreshCcw, Trash2, X } from "lucide-react";
import { GoldImage } from "../GoldImage";
import { Stars, absPhoto, eur, metaPick, svcDesc, svcName } from "./utils";

const Row = ({ label, value }) => (
    value ? (
        <div className="flex justify-between gap-4 border-b border-amber-500/10 py-2 text-xs">
            <span className="shrink-0 text-stone-500">{label}</span>
            <span className="text-right text-stone-200">{value}</span>
        </div>
    ) : null
);

/* Dettaglio servizio: pannello laterale su desktop, schermata piena su mobile.
 * Solo dati reali dello snapshot risolto dal catalogo; azioni reali. */
export const ServiceDetail = ({ item, onClose, onReplace, onRemove, tc, lang }) => {
    if (!item) return null;
    const m = item.meta || {};
    return (
        <div className="fixed inset-0 z-50" data-testid="svc-detail" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <aside className="absolute inset-y-0 right-0 flex w-full flex-col overflow-y-auto border-l border-amber-500/25 bg-[#0d0d0f] sm:w-[440px]">
                <div className="relative h-52 shrink-0">
                    <GoldImage src={absPhoto(item.photo)} alt={svcName(item, lang)} className="absolute inset-0 h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0f] via-transparent to-black/30" />
                    <button onClick={onClose} data-testid="svc-detail-close" aria-label={tc.close}
                        className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/30 bg-black/60 text-amber-100 transition-colors hover:text-amber-300">
                        <X size={16} />
                    </button>
                    <span className="absolute bottom-3 left-4 rounded-full border border-amber-400/50 bg-black/65 px-3 py-1 text-[9px] uppercase tracking-[0.12em] text-amber-200 backdrop-blur-sm">
                        {item.added_by === "ai" ? tc.selectedForYou : tc.addedByYou}
                    </span>
                </div>
                <div className="flex-1 p-5">
                    <h3 className="font-display text-xl text-amber-50" data-testid="svc-detail-name">{svcName(item, lang)}</h3>
                    <div className="mt-2"><Stars rating={m.rating} reviews={m.reviews} /></div>
                    {svcDesc(item, lang) && <p className="mt-3 text-xs leading-relaxed text-stone-300">{svcDesc(item, lang)}</p>}

                    <div className="mt-4">
                        <Row label="Località" value={[m.locality, m.region].filter(Boolean).join(" · ") || m.address || m.destination} />
                        {item.kind === "experience" && <Row label={tc.tabExperiences} value={metaPick(m.type_label, lang)} />}
                        {m.duration_min ? <Row label="Durata" value={`~${Math.round(m.duration_min / 60 * 10) / 10}h · indicativa`} /> : null}
                        {item.kind === "restaurant" && (
                            <>
                                <Row label={tc.cuisineLabel} value={metaPick(m.type_label, lang)} />
                                <Row label={tc.priceRange} value={m.price_level ? `${m.price_level} · ${tc.priceSourceGoogle}` : null} />
                                <Row label="Indirizzo" value={m.address} />
                            </>
                        )}
                        {item.kind === "transfer" && (
                            <>
                                <Row label="Tratta" value={`${metaPick(m.from, lang)} → ${metaPick(m.to, lang)}`} />
                                <Row label="Veicolo" value={metaPick(m.vehicle, lang)} />
                                <Row label="Passeggeri max" value={m.capacity} />
                                <Row label="Bagagli" value={metaPick(m.luggage, lang)} />
                                <Row label="Durata" value={metaPick(m.duration, lang)} />
                                <Row label="Attesa" value={metaPick(m.wait, lang)} />
                            </>
                        )}
                        {item.kind === "package" && <Row label={tc.duration} value={m.duration} />}
                    </div>

                    {m.includes?.length > 0 && (
                        <div className="mt-4">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-500/80 font-semibold">{tc.servicesTitle}</p>
                            <ul className="mt-1.5 space-y-1 text-[11px] text-stone-400">
                                {m.includes.map((x, i) => <li key={i}>· {metaPick(x, lang)}</li>)}
                            </ul>
                        </div>
                    )}
                    {m.conditions && <p className="mt-3 text-[10px] italic leading-relaxed text-stone-500">{m.conditions}</p>}

                    <div className="mt-5 rounded-xl border border-amber-500/20 bg-amber-400/5 p-4 text-center">
                        <p className={`font-display text-lg ${item.price != null || m.price_level ? "text-amber-300" : "text-stone-400"}`} data-testid="svc-detail-price">
                            {item.price != null ? `${eur(item.price)}${item.price_type ? ` · ${item.price_type}` : ""}` : (m.price_level || tc.totalUnknown)}
                        </p>
                        <p className="mt-1 text-[10px] text-stone-500">{item.kind === "restaurant" ? tc.tableNote : tc.disclaimer}</p>
                    </div>

                    <div className="mt-5 flex flex-col gap-2">
                        {item.kind !== "package" && (
                            <button onClick={() => onReplace(item)} data-testid="svc-detail-replace"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-amber-500/40 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-200 transition-colors hover:bg-amber-400/10">
                                <RefreshCcw size={12} /> {tc.replace}
                            </button>
                        )}
                        <button onClick={() => { onRemove(item); onClose(); }} data-testid="svc-detail-remove"
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-red-400/30 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-red-300 transition-colors hover:bg-red-400/10">
                            <Trash2 size={12} /> {tc.remove}
                        </button>
                        <button onClick={onClose} data-testid="svc-detail-back"
                            className="inline-flex items-center justify-center gap-2 py-2 text-[10px] uppercase tracking-[0.14em] text-stone-400 transition-colors hover:text-amber-200">
                            <ArrowLeft size={12} /> {tc.backToTrip}
                        </button>
                    </div>
                </div>
            </aside>
        </div>
    );
};
